/**
 * API Service for OC MENA Festival
 * Handles all communication with the Django backend
 */

// localAdminStore imports removed — local dev now hits local backend directly, same as production

// Treat localhost, 127.0.0.1, and private LAN ranges (e.g. testing on a phone
// over Wi-Fi at 192.168.x.x) as LOCAL DEV. In these cases the API base is the
// relative `/api`, which the CRA dev server proxy (src/setupProxy.js) forwards
// to the local backend at http://localhost:8000. The public production domain
// path is unchanged, so deployed builds still hit the Railway API.
const _isLocalDevHost = () => {
  if (typeof window === 'undefined') return false;
  const h = String(window.location.hostname || '');
  if (h === 'localhost' || h === '127.0.0.1') return true;
  if (/^10\./.test(h)) return true;
  if (/^192\.168\./.test(h)) return true;
  if (/^172\.(1[6-9]|2\d|3[01])\./.test(h)) return true;
  return false;
};

const DEFAULT_API_BASE_URL = _isLocalDevHost()
  ? '/api'
  : 'https://backend-production-18860.up.railway.app/api';

const API_BASE_URL = (process.env.REACT_APP_API_URL || DEFAULT_API_BASE_URL).replace(/^REACT_APP_API_URL\s*=\s*/, '');

export const getApiErrorMessage = (payload, fallback = 'Request failed') => {
  const firstMessage = (value) => {
    if (typeof value === 'string') return value.trim();
    if (Array.isArray(value)) {
      for (const item of value) {
        const message = firstMessage(item);
        if (message) return message;
      }
      return '';
    }
    if (value && typeof value === 'object') {
      for (const key of ['message', 'detail', 'non_field_errors', 'error', 'errors']) {
        const message = firstMessage(value[key]);
        if (message) return message;
      }
      for (const item of Object.values(value)) {
        const message = firstMessage(item);
        if (message) return message;
      }
    }
    return '';
  };

  return firstMessage(payload) || fallback;
};

// Scanner gate subdomains. scan.* is the general gate; blue/green/pacamp are the
// three physical festival gates. The gate a scan happened at is derived from the
// host and sent with every scan so attendance + wristband inventory attribute it.
const SCANNER_GATE_BY_HOST = {
  'scan.ocmenafestival.com': 'Scan',
  'blue.ocmenafestival.com': 'Blue',
  'green.ocmenafestival.com': 'Green',
  'pacamp.ocmenafestival.com': 'PAC AMP',
};

export const getScannerGate = () => {
  if (typeof window === 'undefined') return '';
  return SCANNER_GATE_BY_HOST[String(window.location.hostname || '').toLowerCase()] || '';
};

const isScannerHost = () => getScannerGate() !== '';

class ApiService {
  constructor() {
    // Use sessionStorage for per-tab auth (prevents cross-tab session sharing)
    this.accessToken = sessionStorage.getItem('accessToken');
    this.refreshToken = sessionStorage.getItem('refreshToken');
    this.pendingRegisterPromise = null;
    this.pendingRegisterKey = null;
    this.responseCache = new Map();
    this.pendingBlockedSeatRequests = new Map();
  }

  // Token management - uses sessionStorage for per-tab isolation
  setTokens(access, refresh) {
    this.accessToken = access;
    this.refreshToken = refresh;
    sessionStorage.setItem('accessToken', access);
    sessionStorage.setItem('refreshToken', refresh);
  }

  clearTokens() {
    this.accessToken = null;
    this.refreshToken = null;
    sessionStorage.removeItem('accessToken');
    sessionStorage.removeItem('refreshToken');
    sessionStorage.removeItem('user');
  }

  getUser() {
    const user = sessionStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  }

  setUser(user) {
    sessionStorage.setItem('user', JSON.stringify(user));
  }

  isAuthenticated() {
    return !!this.accessToken;
  }

  getToken() {
    return sessionStorage.getItem('accessToken') || this.accessToken;
  }

  // Base request method
  async request(endpoint, options = {}) {
    const url = `${API_BASE_URL}${endpoint}`;
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    
    const headers = {
      ...options.headers,
    };
    if (!isFormData && !headers['Content-Type'] && !headers['content-type']) {
      headers['Content-Type'] = 'application/json';
    }

    // Always get fresh token from sessionStorage (in case it was updated after login)
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const refreshToken = sessionStorage.getItem('refreshToken') || this.refreshToken;
    const method = String(options.method || 'GET').toUpperCase();
    const cacheTtl = method === 'GET' && !options.noStore ? Math.max(0, Number(options.cacheTtl || 0)) : 0;
    const cacheKey = cacheTtl ? `${url}|${token || 'anonymous'}` : '';
    if (cacheTtl) {
      const cached = this.responseCache.get(cacheKey);
      if (cached && cached.expiresAt > Date.now()) return cached.data;
      if (cached) this.responseCache.delete(cacheKey);
    } else if (method !== 'GET') {
      this.responseCache.clear();
    }
    if (token && !options.skipAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.noStore) {
      headers['Cache-Control'] = headers['Cache-Control'] || 'no-cache';
      headers['Pragma'] = headers['Pragma'] || 'no-cache';
    }

    const fetchOptions = {
      ...options,
      headers,
    };
    if (options.noStore) {
      fetchOptions.cache = 'no-store';
    }
    delete fetchOptions.noStore;
    delete fetchOptions.cacheTtl;

    try {
      const response = await fetch(url, {
        ...fetchOptions,
      });

      // Handle 401 - try to refresh token
      if (response.status === 401 && refreshToken && !options.isRetry) {
        const refreshed = await this.refreshAccessToken();
        if (refreshed) {
          return this.request(endpoint, { ...options, isRetry: true });
        }
        this.clearTokens();
        // Pages with their own auth gate handle the logged-out state themselves
        const selfAuthPages = ['/stadium', '/scanner', '/admin', '/pos'];
        if (isScannerHost()) {
          selfAuthPages.push('/');
        }
        if (!selfAuthPages.includes(window.location.pathname)) {
          window.location.href = '/login';
        }
        return null;
      }

      const contentType = response.headers?.get?.('content-type') || '';
      let data = {};
      if (contentType.includes('application/json') || (!contentType && typeof response.json === 'function')) {
        try {
          data = await response.json();
        } catch {
          data = {};
        }
      } else {
        const text = typeof response.text === 'function' ? await response.text() : '';
        data = text ? { message: text } : {};
      }

      if (!response.ok) {
        const errorMsg = getApiErrorMessage(data, `Request failed (HTTP ${response.status})`);
        const error = new Error(errorMsg);
        error.response = { status: response.status, data };
        error.error = errorMsg; // Also set error property for easier access
        throw error;
      }

      if (cacheTtl) this.responseCache.set(cacheKey, { data, expiresAt: Date.now() + cacheTtl });
      return data;
    } catch (error) {
      console.error(`API Error [${endpoint}]:`, error);
      if (!error.response) {
        error.response = { status: 0, data: {} };
      }
      throw error;
    }
  }

  async refreshAccessToken() {
    try {
      const refreshToken = sessionStorage.getItem('refreshToken') || this.refreshToken;
      if (!refreshToken) return false;

      const response = await fetch(`${API_BASE_URL}/auth/token/refresh/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (!response.ok) return false;

      const data = await response.json();
      this.setTokens(data.access, data.refresh || refreshToken);
      return true;
    } catch {
      return false;
    }
  }

  // ==================== AUTH ====================
  
  async register(email, password, confirmPassword, fullName, phone = '', smsConsent = false, confirmEmail = '') {
    const requestKey = JSON.stringify({
      email: (email || '').trim().toLowerCase(),
      confirmEmail: (confirmEmail || '').trim().toLowerCase(),
      fullName: fullName || '',
      phone: phone || '',
      smsConsent: !!smsConsent,
    });

    if (this.pendingRegisterPromise && this.pendingRegisterKey === requestKey) {
      return this.pendingRegisterPromise;
    }

    const registerPromise = (async () => {
      const data = await this.request('/auth/register/', {
        method: 'POST',
        body: JSON.stringify({
          email,
          confirm_email: confirmEmail,
          password,
          confirm_password: confirmPassword,
          full_name: fullName,
          phone,
          sms_consent: smsConsent,
        }),
        skipAuth: true,
      });

      if (data?.success) {
        this.setTokens(data.data.tokens.access, data.data.tokens.refresh);
        this.setUser(data.data.user);
      }
      return data;
    })();

    this.pendingRegisterPromise = registerPromise;
    this.pendingRegisterKey = requestKey;

    try {
      return await registerPromise;
    } finally {
      if (this.pendingRegisterPromise === registerPromise) {
        this.pendingRegisterPromise = null;
        this.pendingRegisterKey = null;
      }
    }
  }

  async login(email, password) {
    const data = await this.request('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({
        email: String(email || '').trim().toLowerCase(),
        password,
      }),
      skipAuth: true,
    });

    if (data?.success) {
      this.setTokens(data.data.tokens.access, data.data.tokens.refresh);
      this.setUser(data.data.user);
    }
    return data;
  }

  async requestPhoneLoginCode(phone) {
    return this.request('/auth/phone-login/request/', {
      method: 'POST',
      body: JSON.stringify({ phone }),
      skipAuth: true,
    });
  }

  async verifyPhoneLoginCode(phone, code) {
    const data = await this.request('/auth/phone-login/verify/', {
      method: 'POST',
      body: JSON.stringify({ phone, code }),
      skipAuth: true,
    });

    if (data?.success) {
      this.setTokens(data.data.tokens.access, data.data.tokens.refresh);
      this.setUser(data.data.user);
    }
    return data;
  }

  async getPosStaffList() {
    return this.request('/auth/pos-staff/', { skipAuth: true });
  }

  async posStaffLogin(email) {
    const data = await this.request('/auth/pos-login/', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });

    if (data?.success) {
      this.setTokens(data.data.tokens.access, data.data.tokens.refresh);
      this.setUser(data.data.user);
    }
    return data;
  }

  async logout() {
    const accessToken = sessionStorage.getItem('accessToken') || this.accessToken;
    const refreshToken = sessionStorage.getItem('refreshToken') || this.refreshToken;

    try {
      if (accessToken && refreshToken) {
        await this.request('/auth/logout/', {
          method: 'POST',
          body: JSON.stringify({ refresh: refreshToken }),
        });
      }
    } catch (e) {
      // Ignore logout errors - token may already be expired/blacklisted
    } finally {
      this.clearTokens();
    }
  }

  async createAccountFromGuest({ email, password, order_id, sms_consent = false }) {
    const data = await this.request('/auth/create-from-guest/', {
      method: 'POST',
      body: JSON.stringify({ email, password, order_id, sms_consent }),
      skipAuth: true,
    });

    if (data?.success) {
      this.setTokens(data.data.tokens.access, data.data.tokens.refresh);
      this.setUser(data.data.user);
    }
    return data;
  }

  async requestPasswordReset(email) {
    return this.request('/auth/password/reset/', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  }

  async confirmPasswordReset(token, newPassword, confirmPassword) {
    return this.request('/auth/password/reset/confirm/', {
      method: 'POST',
      body: JSON.stringify({ 
        token, 
        new_password: newPassword,
        confirm_password: confirmPassword 
      }),
      skipAuth: true,
    });
  }

  async checkEmail(email) {
    return this.request('/auth/check-email/', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true,
    });
  }

  async changePassword(currentPassword, newPassword) {
    return this.request('/auth/password/change/', {
      method: 'POST',
      body: JSON.stringify({ current_password: currentPassword, new_password: newPassword, confirm_password: newPassword }),
    });
  }

  async getProfile() {
    return this.request('/auth/me/');
  }

  async updateProfile(data) {
    return this.request('/auth/me/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  // ==================== SAVED CARDS ====================

  async getSavedCard() {
    return this.request('/auth/saved-cards/');
  }

  async removeSavedCard() {
    return this.request('/auth/saved-cards/', { method: 'DELETE' });
  }

  async quickCheckout(items, idempotencyKey, coupon, charitySelection = null, extraMeta = null) {
    return this.request('/payments/checkout/quick/', {
      method: 'POST',
      body: JSON.stringify({
        items,
        idempotency_key: idempotencyKey,
        coupon: coupon || null,
        charity_selection: charitySelection || null,
        ...(extraMeta || {}),
      }),
    });
  }

  // ==================== CONFIG ====================

  async getPublicConfig() {
    return this.request('/config/public/', { skipAuth: true, cacheTtl: 60000 });
  }

  async getSponsors() {
    return this.request('/config/sponsors/', { skipAuth: true });
  }

  async getSchedule(day = null, category = null) {
    let endpoint = '/config/schedule/';
    const params = new URLSearchParams();
    if (day) params.append('day', day);
    if (category) params.append('category', category);
    if (params.toString()) endpoint += `?${params}`;
    return this.request(endpoint, { skipAuth: true });
  }

  async submitContact(formData) {
    return this.request('/config/contact/', {
      method: 'POST',
      body: JSON.stringify(formData),
      skipAuth: true,
    });
  }

  // ==================== TICKETS ====================

  async getTicketTypes() {
    // Add cache-busting timestamp and no-cache headers for real-time price sync
    const timestamp = Date.now();
    return this.request(`/tickets/types/?_t=${timestamp}`, { 
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
  }

  async getMyTickets() {
    const timestamp = Date.now();
    return this.request(`/tickets/my/?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
  }

  async getUserTickets() {
    const timestamp = Date.now();
    return this.request(`/tickets/user/tickets/?_t=${timestamp}`, {
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
  }

  async getAllTickets() {
    return this.request('/tickets/all/');
  }

  async getAdminAllTickets(options = {}) {
    const params = new URLSearchParams();
    if (options.eventYear) params.set('event_year', String(options.eventYear));
    if (options.scope) params.set('scope', options.scope);
    if (options.recordState) params.set('record_state', options.recordState);
    if (options.search) params.set('search', options.search);
    if (options.status) params.set('status', options.status);
    if (options.category) params.set('category', options.category);
    if (options.source) params.set('source', options.source);
    if (options.financialClass) params.set('financial_class', options.financialClass);
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('per_page', String(options.perPage));
    if (options.refresh) params.set('refresh', 'true');
    const query = params.toString();
    return this.request(`/tickets/all/${query ? `?${query}` : ''}`, { noStore: true });
  }

  async getAdminDemographics({ eventYear = '', refresh = false } = {}) {
    const params = new URLSearchParams();
    if (eventYear) params.set('event_year', String(eventYear));
    if (refresh) params.set('refresh', 'true');
    const query = params.toString();
    return this.request(`/tickets/admin/demographics/${query ? `?${query}` : ''}`, { noStore: true });
  }

  // Sol — customer-facing festival AI friend
  async getSolBootstrap(lang = 'en') {
    return this.request(`/sol/bootstrap/?lang=${encodeURIComponent(lang)}`, { skipAuth: !this.getToken(), noStore: true });
  }

  async streamSolMessage(payload, onEvent, signal, isRetry = false) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE_URL}/sol/chat/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
      signal,
    });
    if (response.status === 401 && !isRetry && await this.refreshAccessToken()) {
      return this.streamSolMessage(payload, onEvent, signal, true);
    }
    if (!response.ok) {
      let detail = {};
      try { detail = await response.json(); } catch { /* non-JSON response */ }
      throw new Error(getApiErrorMessage(detail, `Sol request failed (HTTP ${response.status})`));
    }
    if (!response.body) throw new Error('Streaming is not supported in this browser.');
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      frames.forEach((frame) => {
        const line = frame.split('\n').find((part) => part.startsWith('data: '));
        if (!line) return;
        try { onEvent(JSON.parse(line.slice(6))); } catch { /* ignore malformed frame */ }
      });
    }
  }

  async confirmSolAction(token, client = {}) {
    return this.request('/sol/confirm/', {
      method: 'POST',
      body: JSON.stringify({ token, client }),
    });
  }

  async sendSolFeedback(payload) {
    return this.request('/sol/feedback/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async assignTicket(assignmentData) {
    return this.request('/tickets/staff/assign/', {
      method: 'POST',
      body: JSON.stringify(assignmentData),
    });
  }

  async getTicketDetail(ticketId) {
    return this.request(`/tickets/${ticketId}/`);
  }

  async getTicketQR(ticketId) {
    return this.request(`/tickets/${ticketId}/qr/`);
  }
  
  // Get base URL for direct links
  getBaseUrl() {
    return API_BASE_URL;
  }

  // Direct PDF downloads - returns URL to download (with auth token)
  getTicketPdfUrl(ticketId) {
    const token = this.getToken();
    const base = `${API_BASE_URL}/tickets/${ticketId}/pdf/`;
    return token ? `${base}?token=${token}` : base;
  }
  
  getOrderPdfUrl(orderId) {
    const token = this.getToken();
    const base = `${API_BASE_URL}/tickets/order/${orderId}/pdf/`;
    return token ? `${base}?token=${token}` : base;
  }

  getInvoicePdfUrl(orderId) {
    const token = this.getToken();
    const base = `${API_BASE_URL}/tickets/order/${orderId}/invoice-pdf/`;
    return token ? `${base}?token=${token}` : base;
  }
  
  // ==================== WALLET PASSES ====================
  
  // Detect iOS in-app browsers (Instagram, Facebook, Twitter, etc.)
  isIOSInAppBrowser() {
    const ua = navigator.userAgent || navigator.vendor;
    const isIOS = /iPad|iPhone|iPod/.test(ua);
    if (!isIOS) return false;

    // Apple Wallet pass install is reliable only in iOS Safari.
    // Treat every non-Safari iOS browser/webview as "in-app" for this flow.
    const isSafari = /Safari/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS|DuckDuckGo|YaBrowser/i.test(ua);

    // Common in-app browser signatures (social + email + embedded webviews).
    const inAppPatterns = [
      /FBAN|FBAV/i,           // Facebook
      /Instagram/i,           // Instagram
      /Twitter/i,             // Twitter / X
      /Line\//i,              // Line
      /KAKAOTALK/i,           // KakaoTalk
      /Snapchat/i,            // Snapchat
      /WhatsApp/i,            // WhatsApp
      /Pinterest/i,           // Pinterest
      /LinkedIn/i,            // LinkedIn
      /GSA/i,                 // Google app
      /Gmail/i,               // Gmail iOS in-app browser
      /Outlook/i,             // Outlook in-app browser
      /YahooMail/i,           // Yahoo Mail in-app browser
    ];

    const hasInAppSignature = inAppPatterns.some(pattern => pattern.test(ua));
    return hasInAppSignature || !isSafari;
  }
  
  // Check if device is iOS
  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent);
  }
  
  // Create Apple Wallet pass - two-step: create pass via API, then open download URL
  // Step 1: POST /api/wallet/apple/create-pass/ (authenticated) → generates pass, returns download_url
  // Step 2: Browser navigates to download_url → serves .pkpass with proper HTTP headers
  // This avoids blob URLs which Safari/iOS cannot handle for .pkpass files
  async createAppleWalletPass(ticketId) {
    console.log('[AppleWallet] createAppleWalletPass called for ticket:', ticketId);
    
    // Check for iOS in-app browser
    if (this.isIOSInAppBrowser()) {
      console.log('[AppleWallet] Detected in-app browser, blocking');
      return {
        success: false,
        inAppBrowser: true,
        error: { 
          message: 'Apple Wallet requires Safari. Please tap the Share button (↑) and select "Open in Safari".',
          code: 'IN_APP_BROWSER'
        }
      };
    }
    
    try {
      // Step 1: Create the pass via authenticated API call
      const response = await this.request('/wallet/apple/create-pass/', {
        method: 'POST',
        body: JSON.stringify({ ticket_id: ticketId })
      });
      
      if (response?.success && response?.download_url) {
        console.log('[AppleWallet] Pass created, download URL:', response.download_url);
        
        // Return download_url to caller - let handler manage window opening
        // to avoid popup blocker issues with window.open after await
        return { success: true, download_url: response.download_url };
      }
      
      return {
        success: false,
        error: response?.error || { message: 'Failed to generate Apple Wallet pass.' }
      };
    } catch (err) {
      console.error('[AppleWallet] Error:', err);
      return {
        success: false,
        error: { message: err.message || 'Unable to generate Apple Wallet pass. Please try again.' }
      };
    }
  }
  
  // Create Google Wallet pass
  async createGoogleWalletPass(ticketId) {
    return this.request('/wallet/google/create-pass/', {
      method: 'POST',
      body: JSON.stringify({ ticket_id: ticketId })
    });
  }

  // ── FIFA-only wallet passes ───────────────────────────────────────
  // FIFA buyers check out anonymously (no account). These endpoints
  // trust the Stripe session_id the same way /orders/by-session/ does,
  // so we skip auth and pass the session explicitly. Response shape
  // mirrors the authenticated endpoints: Apple → {download_url},
  // Google → {save_url}.
  async createFifaAppleWalletPass(sessionId, ticketId) {
    console.log('[FifaAppleWallet] createFifaAppleWalletPass:', ticketId);

    // In-app browsers can't handle .pkpass — same guard as the
    // authenticated flow so customers on IG / TikTok get a useful msg.
    if (this.isIOSInAppBrowser && this.isIOSInAppBrowser()) {
      return {
        success: false,
        inAppBrowser: true,
        error: {
          message: 'Apple Wallet requires Safari. Tap the Share button (↑) and pick "Open in Safari".',
          code: 'IN_APP_BROWSER',
        },
      };
    }

    try {
      const response = await this.request('/wallet/fifa/apple-pass/', {
        method: 'POST',
        skipAuth: true,
        body: JSON.stringify({ session_id: sessionId, ticket_id: ticketId }),
      });
      if (response?.success && response?.download_url) {
        return { success: true, download_url: response.download_url };
      }
      return {
        success: false,
        error: response?.error || { message: 'Failed to generate Apple Wallet pass.' },
      };
    } catch (err) {
      return {
        success: false,
        error: { message: err.message || 'Unable to generate Apple Wallet pass.' },
      };
    }
  }

  async createFifaGoogleWalletPass(sessionId, ticketId) {
    return this.request('/wallet/fifa/google-pass/', {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ session_id: sessionId, ticket_id: ticketId }),
    });
  }
  
  // Get user's wallet passes
  async getMyWalletPasses() {
    return this.request('/wallet/passes/');
  }
  
  // Wallet URL methods
  getWalletPassUrl(ticketId) {
    return `${API_BASE_URL}/wallet/apple/create-pass/`;
  }
  
  getAppleWalletUrl(ticketId) {
    return `${API_BASE_URL}/wallet/apple/create-pass/`;
  }
  
  getGoogleWalletUrl(ticketId) {
    return `${API_BASE_URL}/wallet/google/create-pass/`;
  }

  // ==================== TRANSFERS ====================

  async getMyTransfers() {
    return this.request('/tickets/transfers/');
  }

  async createTransfer(ticketId, toEmail, giftLabel = '') {
    return this.request('/tickets/transfers/create/', {
      method: 'POST',
      body: JSON.stringify({ ticket_id: ticketId, to_email: toEmail, gift_label: giftLabel }),
    });
  }

  async createBulkTransfer(ticketIds, toEmail, giftLabel = '') {
    return this.request('/tickets/transfers/create-bulk/', {
      method: 'POST',
      body: JSON.stringify({ ticket_ids: ticketIds, to_email: toEmail, gift_label: giftLabel }),
    });
  }

  async cancelTransfer(transferId) {
    return this.request(`/tickets/transfers/${transferId}/cancel/`, {
      method: 'POST',
    });
  }

  async acceptTransfer(token) {
    return this.request('/tickets/transfers/accept/', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }

  async acceptTransferById(transferId) {
    return this.request(`/tickets/transfers/${transferId}/accept/`, {
      method: 'POST',
    });
  }

  async returnTransfer(transferId) {
    return this.request(`/tickets/transfers/${transferId}/return/`, {
      method: 'POST',
    });
  }

  // ==================== PUBLIC TICKET (no auth) ====================

  async getPublicTicket(ticketCode) {
    return this.request(`/tickets/public/${ticketCode}/`, { skipAuth: true });
  }

  getPublicAppleWalletUrl(ticketId, ticketCode) {
    return `${API_BASE_URL}/tickets/${ticketId}/apple-wallet/?code=${ticketCode}`;
  }

  async getPublicGoogleWalletPass(ticketId, ticketCode) {
    return this.request(`/tickets/${ticketId}/google-wallet/?code=${ticketCode}`, {
      method: 'POST',
      skipAuth: true,
      body: JSON.stringify({ code: ticketCode }),
    });
  }

  async getPublicTicketCollection(token) {
    return this.request(`/tickets/public/collection/${token}/`, { skipAuth: true });
  }

  // ==================== PAYMENTS ====================

  async createPaymentIntent(items, idempotencyKey, paymentMethod = 'card') {
    return this.request('/payments/checkout/create-intent/', {
      method: 'POST',
      body: JSON.stringify({ 
        items, 
        idempotency_key: idempotencyKey,
        payment_method: paymentMethod
      }),
    });
  }

  async createCheckoutSession(formData, items, idempotencyKey, coupon = null, charitySelection = null, extraMeta = null) {
    return this.request('/payments/checkout/create-session/', {
      method: 'POST',
      body: JSON.stringify({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone,
        items: items,
        idempotency_key: idempotencyKey,
        coupon: coupon,
        charity_selection: charitySelection || null,
        ...(extraMeta || {})
      }),
      skipAuth: true
    });
  }

  async confirmPayment(orderId, paymentIntentId) {
    return this.request('/payments/checkout/confirm/', {
      method: 'POST',
      body: JSON.stringify({ 
        order_id: orderId,
        payment_intent_id: paymentIntentId
      }),
    });
  }

  async checkDemoMode() {
    return this.request('/payments/checkout/demo-mode/', { skipAuth: true });
  }

  async getMyOrders() {
    return this.request('/payments/orders/');
  }

  async getOrderDetail(orderId) {
    return this.request(`/payments/orders/${orderId}/`);
  }

  async getOrderBySessionId(sessionId) {
    return this.request(`/payments/orders/by-session/${sessionId}/`, { skipAuth: true });
  }

  // ==================== POST-PURCHASE UPSELL ====================

  async getUpsellOffer(sessionId) {
    return this.request(`/payments/upsell/${encodeURIComponent(sessionId)}/`, { skipAuth: true });
  }

  async declineUpsellOffer(sessionId) {
    return this.request(`/payments/upsell/${encodeURIComponent(sessionId)}/decline/`, {
      method: 'POST',
      body: JSON.stringify({}),
      skipAuth: true,
    });
  }

  async closeUpsellOffer(sessionId) {
    return this.request(`/payments/upsell/${encodeURIComponent(sessionId)}/close/`, {
      method: 'POST',
      body: JSON.stringify({}),
      skipAuth: true,
    });
  }

  async trackUpsellAction(sessionId, action, payload = {}) {
    return this.request(`/payments/upsell/${encodeURIComponent(sessionId)}/track/`, {
      method: 'POST',
      body: JSON.stringify({ action, payload }),
      skipAuth: true,
    });
  }

  async acceptUpsellPass(sessionId, passSlug, quantity) {
    return this.request(`/payments/upsell/${encodeURIComponent(sessionId)}/accept-pass/`, {
      method: 'POST',
      body: JSON.stringify({ pass_slug: passSlug, quantity }),
      skipAuth: true,
    });
  }

  async getUpsellAdminStats(eventYear = '') {
    const params = new URLSearchParams();
    if (eventYear) params.set('event_year', String(eventYear));
    const qs = params.toString();
    return this.request(`/payments/upsell/admin/stats/${qs ? `?${qs}` : ''}`);
  }

  // ==================== PDF DOWNLOADS ====================

  getTicketPDFUrl(ticketId) {
    const token = this.getToken();
    const base = `${API_BASE_URL}/tickets/${ticketId}/pdf/`;
    return token ? `${base}?token=${token}` : base;
  }

  getOrderTicketsPDFUrl(orderId) {
    const token = this.getToken();
    const base = `${API_BASE_URL}/tickets/order/${orderId}/pdf/`;
    return token ? `${base}?token=${token}` : base;
  }

  async downloadTicketPDF(ticketId) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE_URL}/tickets/${ticketId}/pdf/`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    if (!response.ok) throw new Error('Failed to download PDF');
    return response.blob();
  }

  async downloadOrderTicketsPDF(orderId) {
    // No authentication required for order success page downloads
    const response = await fetch(`${API_BASE_URL}/tickets/order/${orderId}/pdf/`, {
      method: 'GET',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    
    // Check if response is actually a PDF
    const contentType = response.headers.get('content-type');
    
    if (!response.ok) {
      let errorMessage = `Failed to download PDF: ${response.status}`;
      try {
        // Try to parse error response
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          errorMessage = errorData?.error?.message || errorMessage;
        } else {
          const errorText = await response.text();
          console.error('PDF download failed:', response.status, errorText);
        }
      } catch (e) {
        console.error('Error parsing error response:', e);
      }
      throw new Error(errorMessage);
    }
    
    // Verify it's actually a PDF
    if (!contentType || !contentType.includes('application/pdf')) {
      console.error('Response is not a PDF, content-type:', contentType);
      throw new Error('Server did not return a valid PDF file');
    }
    
    return response.blob();
  }

  // ==================== VENDORS ====================

  async getPublicVendors() {
    return this.request('/vendors/list/', { skipAuth: true });
  }

  async getVendorProfile() {
    return this.request('/vendors/profile/');
  }

  async getVendorDashboard() {
    return this.request('/vendors/dashboard/');
  }

  async submitBazaarVendor(data) {
    // Handle FormData for file uploads
    const url = `${API_BASE_URL}/vendors/bazaar-registration/`;
    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData - browser will set it with boundary
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: data // FormData or regular object
    });
    
    return response.json();
  }

  async submitFoodVendor(data) {
    // Handle FormData for file uploads
    const url = `${API_BASE_URL}/vendors/food-registration/`;
    const headers = {};
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    // Don't set Content-Type for FormData - browser will set it with boundary
    
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: data // FormData or regular object
    });
    
    return response.json();
  }

  async checkVendorEligibility(email) {
    return this.request(`/vendors/check-eligibility/?email=${encodeURIComponent(email)}`);
  }

  async getVendorTicketTypes() {
    // Add cache-busting timestamp and no-cache headers for real-time price sync
    const timestamp = Date.now();
    return this.request(`/tickets/vendor-types/?_t=${timestamp}`, { 
      headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache'
      }
    });
  }

  async getBazaarRegistrations() {
    return this.request('/vendors/admin/bazaar-registrations/');
  }

  async getFoodRegistrations() {
    return this.request('/vendors/admin/food-registrations/');
  }

  async deleteVendorRegistration(registrationId, type) {
    const params = type ? `?type=${type}` : '';
    return this.request(`/vendors/admin/registrations/${registrationId}/${params}`, {
      method: 'DELETE'
    });
  }

  async bulkDeleteVendorRegistrations(emails) {
    return this.request('/vendors/admin/registrations/bulk-delete/', {
      method: 'POST',
      body: JSON.stringify({ emails })
    });
  }

  // ==================== ADMIN ORDERS ====================

  async getAdminOrders(options = {}) {
    const params = new URLSearchParams();
    if (options.lite) params.set('lite', 'true');
    if (options.includeItems) params.set('include_items', 'true');
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('per_page', String(options.perPage));
    if (options.status) params.set('status', options.status);
    if (options.search) params.set('search', options.search);
    if (options.ticketType) params.set('ticket_type', options.ticketType);
    if (options.section) params.set('section', options.section);
    if (options.row) params.set('row', options.row);
    if (options.email) params.set('email', options.email);
    if (options.excludeCoupon) params.set('exclude_coupon', 'true');
    if (options.excludeHidden) params.set('exclude_hidden', 'true');
    if (options.eventYear) params.set('event_year', String(options.eventYear));
    const query = params.toString();
    return this.request(`/tickets/staff/orders/${query ? `?${query}` : ''}`, { cacheTtl: 10000 });
  }

  async getAdminOrderDetail(orderId, options = {}) {
    const params = options.syncStripe === false ? '?sync_stripe=false' : '';
    return this.request(`/tickets/staff/orders/${orderId}/${params}`, { cacheTtl: options.syncStripe === false ? 15000 : 0 });
  }

  async getAdminTransfers(options = {}) {
    const params = new URLSearchParams();
    if (options.page) params.set('page', String(options.page));
    if (options.per_page) params.set('per_page', String(options.per_page));
    if (options.status) params.set('status', options.status);
    if (options.search) params.set('search', options.search);
    const query = params.toString();
    return this.request(`/tickets/staff/transfers/${query ? `?${query}` : ''}`);
  }

  async processRefund(orderId, reason) {
    return this.request('/tickets/staff/refund/', {
      method: 'POST',
      body: JSON.stringify({ order_id: orderId, reason })
    });
  }

  async issueCompTicket(email, ticketTypeId, quantity, reason) {
    return this.request('/tickets/staff/comp/', {
      method: 'POST',
      body: JSON.stringify({ 
        email, 
        ticket_type_id: ticketTypeId, 
        quantity,
        reason 
      })
    });
  }

  // ==================== COMP TRANSFERS ====================

  async adminCompTransfer(data) {
    return this.request('/tickets/coordinator/comp-transfer/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ==================== AMPHITHEATER ====================

  async getAmphitheaterEventDates() {
    return this.request('/tickets/amphitheater/event-dates/');
  }

  async getAdminAmpSeats({ eventDate, section, row, seatNumber, search, boxOnly = false, page = 1, pageSize = 20 } = {}) {
    const p = new URLSearchParams();
    if (eventDate) p.append('event_date', eventDate);
    if (section) p.append('section', section);
    if (row) p.append('row', row);
    if (seatNumber) p.append('seat_number', seatNumber);
    if (search) p.append('search', search);
    if (boxOnly) p.append('box_only', 'true');
    p.append('page', page);
    p.append('page_size', pageSize);
    return this.request(`/tickets/admin/amp-seats/?${p.toString()}`);
  }

  async getAdminBoxSeatNotes() {
    return this.request('/tickets/admin/box-seat-notes/');
  }

  async saveAdminBoxSeatNote(key, note) {
    return this.request('/tickets/admin/box-seat-notes/', {
      method: 'PATCH',
      body: JSON.stringify({ key, note }),
    });
  }

  async getAdminFestivalTickets({ passType, search, eventYear, page = 1, pageSize = 20, refresh = false } = {}) {
    const p = new URLSearchParams();
    if (passType) p.append('pass_type', passType);
    if (search) p.append('search', search);
    if (eventYear) p.append('event_year', String(eventYear));
    if (refresh) p.append('refresh', '1');
    p.append('page', page);
    p.append('page_size', pageSize);
    return this.request(`/tickets/admin/festival-tickets/?${p.toString()}`, refresh ? { noStore: true } : { cacheTtl: 30000 });
  }

  async getAdminFestivalPassTickets({ passType, search, eventYear, page = 1, pageSize = 20 } = {}) {
    const p = new URLSearchParams();
    if (passType) p.append('pass_type', passType);
    if (search) p.append('search', search);
    if (eventYear) p.append('event_year', String(eventYear));
    p.append('page', page);
    p.append('page_size', pageSize);
    return this.request(`/tickets/admin/festival-pass-tickets/?${p.toString()}`);
  }

  async generateAdminAmpSnapshots(day = 'both', store = true) {
    return this.request('/tickets/admin/amp-snapshots/generate/', {
      method: 'POST',
      body: JSON.stringify({ day, store }),
    });
  }

  async getAmpSnapshotSections(day = 'saturday') {
    return this.request(`/tickets/admin/amp-snapshots/preview/?day=${day}`);
  }

  getSnapshotImageUrl(day, section, cacheKey) {
    return `${API_BASE_URL}/tickets/admin/amp-snapshots/image/?day=${encodeURIComponent(day)}&section=${encodeURIComponent(section)}${cacheKey ? '&t=' + cacheKey : ''}`;
  }

  getSnapshotImageUrlByPath(storagePath) {
    return `${API_BASE_URL}/tickets/admin/amp-snapshots/image/?path=${encodeURIComponent(storagePath)}`;
  }

  async downloadAdminAmpSnapshot(day = 'saturday', store = true, section = 'overview') {
    const token = this.getToken();
    const params = new URLSearchParams();
    params.set('day', day);
    params.set('store', store ? 'true' : 'false');
    params.set('section', section);
    const response = await fetch(`${API_BASE_URL}/tickets/admin/amp-snapshots/download/?${params.toString()}`, {
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });

    if (!response.ok) {
      let errMsg = 'Failed to download AMP snapshot.';
      try {
        const data = await response.json();
        errMsg = data?.error?.message || data?.message || errMsg;
      } catch (_) {}
      throw new Error(errMsg);
    }

    const disposition = response.headers.get('content-disposition') || '';
    const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
    const filename = filenameMatch?.[1] || `PacAmp_${day}_Snapshot.png`;
    const blob = await response.blob();
    return { blob, filename };
  }

  async getAmphitheaterSeats(eventDate, { includeAvailable = false } = {}) {
    let params = eventDate ? `?event_date=${eventDate}` : '';
    if (includeAvailable) params += (params ? '&' : '?') + 'include_available=true';
    return this.request(`/tickets/amphitheater/seats/availability/${params}`);
  }

  async createSeatHold(sectionId, eventDate, quantity, sessionKey, seats = null) {
    const payload = { 
      section_id: sectionId, 
      event_date: eventDate,
      quantity,
      session_key: sessionKey
    };
    // Include specific seats if provided (row and seat number for each)
    if (seats && seats.length > 0) {
      payload.seats = seats.map(s => ({ row: s.row, seat: s.number }));
      console.log('Sending specific seats to backend:', payload.seats);
    } else {
      console.log('No specific seats provided, seats param:', seats);
    }
    return this.request('/tickets/amphitheater/seats/hold/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async releaseSeatHold(holdId) {
    return this.request(`/tickets/amphitheater/holds/${holdId}/release/`, {
      method: 'POST'
    });
  }

  async holdAmphitheaterSeats(seatIds, eventDate) {
    return this.request('/amphitheater/hold/', {
      method: 'POST',
      body: JSON.stringify({ seat_ids: seatIds, event_date: eventDate })
    });
  }

  async checkoutAmphitheater(holdId, billingDetails) {
    return this.request('/amphitheater/checkout/', {
      method: 'POST',
      body: JSON.stringify({ hold_id: holdId, billing_details: billingDetails })
    });
  }

  async getMyAmphitheaterTickets() {
    return this.request('/amphitheater/tickets/');
  }

  // ==================== AMP PRICING ADMIN ====================

  async getAmpPricing(options = {}) {
    const params = new URLSearchParams();
    if (options.includeSeats) params.set('include_seats', 'true');
    if (options.day) params.set('day', options.day);
    const query = params.toString();
    return this.request(`/config/admin/amp-pricing/${query ? `?${query}` : ''}`);
  }

  async updateAmpPricing(data) {
    return this.request('/config/admin/amp-pricing/', {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async saveAmpPricingModel(pricingModel) {
    return this.request('/config/admin/amp-pricing/', {
      method: 'PATCH',
      body: JSON.stringify({ pricing_model: pricingModel }),
    });
  }

  // ==================== SCANNING ====================

  async quickScan(qrData) {
    return this.request('/scan/quick/', {
      method: 'POST',
      body: JSON.stringify({ qr_data: qrData }),
      skipAuth: true
    });
  }

  async validateScan(qrData, scanEventDate = null) {
    const payload = { qr_data: qrData };
    if (scanEventDate) payload.scan_event_date = scanEventDate;
    const gate = getScannerGate();
    if (gate) payload.gate = gate;
    return this.request('/scan/validate/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async commitScan(ticketCode, scanMode = 'both', scanEventDate = null, qrData = '') {
    const payload = { ticket_code: ticketCode, scan_mode: scanMode };
    // Camera scans must forward the raw QR string so the backend can reject
    // screenshots / stale rotating tokens at the entry path too.
    if (qrData) payload.qr_data = qrData;
    if (scanEventDate) payload.scan_event_date = scanEventDate;
    const gate = getScannerGate();
    if (gate) payload.gate = gate;
    return this.request('/scan/commit/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async resetTestQrStates(ticketCode = '', person = '') {
    const payload = {};
    if (ticketCode) payload.ticket_code = ticketCode;
    if (person) payload.person = String(person);
    return this.request('/scan/test-qr/reset/', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  }

  async searchManualScanUsers(query) {
    const params = new URLSearchParams({ query: String(query || '') });
    return this.request(`/scan/manual-users/?${params.toString()}`);
  }

  async getManualScanUserTickets(userId, scanEventDate = null) {
    const params = new URLSearchParams({ user_id: String(userId || '') });
    if (scanEventDate) params.set('scan_event_date', scanEventDate);
    return this.request(`/scan/manual-user-tickets/?${params.toString()}`);
  }

  async commitManualScanGroup(userId, groupKey, scanEventDate = null) {
    const payload = {
      user_id: userId,
      group_key: groupKey,
    };
    if (scanEventDate) payload.scan_event_date = scanEventDate;
    return this.request('/scan/manual-commit-group/', {
      method: 'POST',
      body: JSON.stringify(payload)
    });
  }

  async adminVipPromote(ticketId, isVip = true) {
    return this.request(`/tickets/admin/ticket/${ticketId}/vip/`, {
      method: 'POST',
      body: JSON.stringify({ is_vip: isVip })
    });
  }

  async adminTransferTicket(ticketId, toEmail) {
    return this.request(`/tickets/admin/ticket/${ticketId}/transfer/`, {
      method: 'POST',
      body: JSON.stringify({ to_email: toEmail })
    });
  }

  async adminRevokeTicket(ticketCode, reason) {
    return this.request('/tickets/coordinator/reset-seats/', {
      method: 'POST',
      body: JSON.stringify({ action: 'void_ticket', ticket_code: ticketCode, reason })
    });
  }

  async getScanLogs(params = {}) {
    const queryString = new URLSearchParams(params).toString();
    return this.request(`/scan/logs/${queryString ? '?' + queryString : ''}`);
  }

  async getScanStats() {
    return this.request('/scan/stats/');
  }

  async getDayEstimation(eventYear = '') {
    const params = new URLSearchParams();
    if (eventYear) params.set('event_year', String(eventYear));
    const query = params.toString();
    return this.request(`/scan/day-estimation/${query ? `?${query}` : ''}`, { cacheTtl: 30000 });
  }

  async getAttendance({ refresh = false, eventYear = '' } = {}) {
    const params = new URLSearchParams();
    if (refresh) params.set('refresh', '1');
    if (eventYear) params.set('event_year', String(eventYear));
    const qs = params.toString();
    return this.request(`/scan/attendance/${qs ? `?${qs}` : ''}`, { noStore: true });
  }

  async getAttendanceAnalytics({ day = '', refresh = false, eventYear = '' } = {}) {
    const params = new URLSearchParams();
    if (day) params.set('day', String(day));
    if (refresh) params.set('refresh', '1');
    if (eventYear) params.set('event_year', String(eventYear));
    const qs = params.toString();
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 20000);
    try {
      return await this.request(`/scan/attendance/analytics/${qs ? `?${qs}` : ''}`, refresh
        ? { noStore: true, signal: controller.signal }
        : { cacheTtl: 12000, signal: controller.signal });
    } catch (error) {
      if (error?.name === 'AbortError') throw new Error('Attendance analytics took too long to respond. Please retry.');
      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  async getAttendanceRecentScans({ page = 1, perPage = 25, search = '', eventYear = '' } = {}) {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (search) params.set('search', search);
    if (eventYear) params.set('event_year', String(eventYear));
    return this.request(`/scan/attendance/recent/?${params.toString()}`, { noStore: true });
  }

  async getWristbandInventory({ refresh = false, eventYear = '' } = {}) {
    const params = new URLSearchParams();
    if (eventYear) params.set('event_year', String(eventYear));
    if (refresh) {
      params.set('refresh', '1');
      params.set('t', String(Date.now()));
    }
    const qs = params.toString();
    return this.request(`/scan/wristband-inventory/${qs ? `?${qs}` : ''}`, { noStore: true });
  }

  async saveWristbandInventory(payload) {
    return this.request('/scan/wristband-inventory/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  // ==================== NEWSLETTER ====================

  async subscribeNewsletter(email) {
    return this.request('/tickets/newsletter/signup/', {
      method: 'POST',
      body: JSON.stringify({ email }),
      skipAuth: true
    });
  }

  // ==================== COUPONS ====================

  async getCoupons(lite = false, options = {}) {
    if (typeof lite === 'object') {
      options = lite;
      lite = Boolean(options.lite);
    }
    const params = new URLSearchParams();
    if (lite) params.set('lite', 'true');
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('per_page', String(options.perPage));
    if (options.search) params.set('search', String(options.search));
    if (options.scope) params.set('scope', String(options.scope));
    const query = params.toString();
    return this.request(`/payments/coupons/${query ? `?${query}` : ''}`);
  }

  async createCoupon(couponData) {
    return this.request('/payments/coupons/', {
      method: 'POST',
      body: JSON.stringify(couponData)
    });
  }

  async updateCoupon(couponId, couponData) {
    return this.request(`/payments/coupons/${couponId}/`, {
      method: 'PATCH',
      body: JSON.stringify(couponData)
    });
  }

  async deleteCoupon(couponId) {
    return this.request(`/payments/coupons/${couponId}/`, {
      method: 'DELETE'
    });
  }

  async validateCoupon(code, items = null) {
    // NOTE: no `skipAuth` here — we deliberately forward the logged-in
    // user's JWT so the backend can enforce per-buyer restrictions on
    // upsell coupons (`Coupon.restricted_to_email`). Guests fall through
    // and get no Authorization header, which correctly rejects any
    // email-restricted coupon they try.
    return this.request('/payments/coupons/validate/', {
      method: 'POST',
      body: JSON.stringify({
        code: code,
        items: items,
      }),
    });
  }

  // ==================== TICKET TYPE MANAGEMENT ====================

  async getAdminTicketTypes(options = {}) {
    const params = new URLSearchParams();
    if (options.eventYear) params.set('event_year', String(options.eventYear));
    const query = params.toString();
    return this.request(`/tickets/admin/ticket-types/${query ? `?${query}` : ''}`, { cacheTtl: 30000 });
  }

  async updateTicketType(ticketTypeId, data) {
    return this.request(`/tickets/types/${ticketTypeId}/update/`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  }

  // ==================== VENDOR DOCUMENTS ====================

  async getVendorDocuments() {
    return this.request('/vendors/documents/');
  }

  async uploadVendorDocument(documentType, file, expirationDate = null, _isRetry = false) {
    const formData = new FormData();
    formData.append('document_type', documentType);
    formData.append('file', file);
    if (expirationDate) {
      formData.append('expiration_date', expirationDate);
    }

    const url = `${API_BASE_URL}/vendors/documents/`;
    // Always get fresh token from sessionStorage (not stale this.accessToken)
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    console.log(`[api.uploadVendorDocument] POST ${url}, hasToken=${!!token}, file=${file.name}, type=${documentType}`);

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });

    // Handle 401 - try token refresh (like this.request does)
    if (response.status === 401 && !_isRetry && this.refreshToken) {
      console.log('[api.uploadVendorDocument] 401 - attempting token refresh...');
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        console.log('[api.uploadVendorDocument] Token refreshed, retrying upload...');
        return this.uploadVendorDocument(documentType, file, expirationDate, true);
      }
      console.error('[api.uploadVendorDocument] Token refresh failed');
    }

    const data = await response.json();
    if (!response.ok) {
      console.error(`[api.uploadVendorDocument] Error ${response.status}:`, data);
    }
    return data;
  }

  async staffUploadVendorDocument(vendorEmail, documentType, file, _isRetry = false) {
    const formData = new FormData();
    formData.append('vendor_email', vendorEmail);
    formData.append('document_type', documentType);
    formData.append('file', file);

    const url = `${API_BASE_URL}/vendors/admin/documents/upload/`;
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: formData
    });

    if (response.status === 401 && !_isRetry && this.refreshToken) {
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        return this.staffUploadVendorDocument(vendorEmail, documentType, file, true);
      }
    }

    const data = await response.json();
    return data;
  }

  async deleteVendorDocument(documentId) {
    return this.request(`/vendors/documents/${documentId}/`, {
      method: 'DELETE'
    });
  }

  getVendorDocumentDownloadUrl(documentId) {
    // Include token in URL for direct downloads (e.g., opening in new tab)
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const baseUrl = `${API_BASE_URL}/vendors/documents/${documentId}/download/`;
    return token ? `${baseUrl}?token=${token}` : baseUrl;
  }

  async signVendorContract() {
    return this.request('/vendors/contract/sign/', {
      method: 'POST'
    });
  }

  // DocuSign integration
  async createDocuSignContract(returnUrl = null) {
    const data = returnUrl ? { return_url: returnUrl } : {};
    return this.request('/vendors/docusign/contract/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getDocuSignStatus(envelopeId) {
    return this.request(`/vendors/docusign/status/${envelopeId}/`);
  }

  async resendDocuSignEnvelope(envelopeId) {
    return this.request(`/vendors/docusign/resend/${envelopeId}/`, {
      method: 'POST'
    });
  }

  async completeDocuSignContract() {
    // Called after returning from DocuSign to finalize the contract
    return this.request('/vendors/docusign/complete/', {
      method: 'POST'
    });
  }

  // Admin vendor document methods
  async getAdminVendorDocuments(filters = {}) {
    const params = new URLSearchParams();
    if (filters.status) params.append('status', filters.status);
    if (filters.type) params.append('type', filters.type);
    if (filters.email) params.append('email', filters.email);
    
    const queryString = params.toString();
    return this.request(`/vendors/admin/documents/${queryString ? '?' + queryString : ''}`);
  }

  async validateVendorDocument(documentId, validationStatus, notes = '', expirationDate = null) {
    const data = {
      validation_status: validationStatus,
      validation_notes: notes
    };
    if (expirationDate) {
      data.expiration_date = expirationDate;
    }
    return this.request(`/vendors/admin/documents/${documentId}/validate/`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getVendorDropboxFolder(email) {
    return this.request(`/vendors/admin/dropbox-folder/?email=${encodeURIComponent(email)}`);
  }

  // Admin charity methods
  async getAdminCharityData() {
    return this.request('/vendors/admin/charities/');
  }

  async toggleCharityPaidStatus(charityKey, isPaid) {
    return this.request('/vendors/admin/charities/', {
      method: 'POST',
      body: JSON.stringify({ charity_key: charityKey, is_paid: isPaid })
    });
  }

  // Admin: View as user (preview their dashboard)
  async adminViewAsUser(email) {
    return this.request(`/tickets/admin/view-as-user/?email=${encodeURIComponent(email)}`);
  }

  // Admin assistant (admin-only, every side effect requires a server-side approval)
  async getAdminAssistantStatus() {
    return this.request('/admin/assistant/status/');
  }

  async getAdminAssistantConversations() {
    return this.request('/admin/assistant/conversations/');
  }

  async createAdminAssistantConversation(eventYear = null) {
    return this.request('/admin/assistant/conversations/', {
      method: 'POST',
      body: JSON.stringify(eventYear ? { event_year: eventYear } : {}),
    });
  }

  async getAdminAssistantConversation(conversationId) {
    return this.request(`/admin/assistant/conversations/${conversationId}/`);
  }

  async deleteAdminAssistantConversation(conversationId) {
    return this.request(`/admin/assistant/conversations/${conversationId}/`, { method: 'DELETE' });
  }

  async sendAdminAssistantMessage(conversationId, message, history = [], testMode = false, idempotencyKey = '', signal) {
    return this.request(`/admin/assistant/conversations/${conversationId}/messages/`, {
      method: 'POST',
      body: JSON.stringify({ message, history, test_mode: testMode, idempotency_key: idempotencyKey }),
      signal,
    });
  }

  async streamAdminAssistantMessage(conversationId, message, history = [], testMode = false, idempotencyKey = '', onEvent, signal) {
    const token = this.getToken();
    const response = await fetch(`${API_BASE_URL}/admin/assistant/conversations/${conversationId}/messages/stream/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
      body: JSON.stringify({ message, history, test_mode: testMode, idempotency_key: idempotencyKey }),
      signal,
    });
    if (!response.ok || !response.body) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(getApiErrorMessage(payload, 'HQ Copilot streaming failed.'));
    }
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let complete = null;
    while (true) {
      const { value, done } = await reader.read();
      buffer += decoder.decode(value || new Uint8Array(), { stream: !done });
      const frames = buffer.split('\n\n');
      buffer = frames.pop() || '';
      for (const frame of frames) {
        const line = frame.split('\n').find((item) => item.startsWith('data: '));
        if (!line) continue;
        const event = JSON.parse(line.slice(6));
        if (onEvent) onEvent(event);
        if (event.type === 'error') throw new Error(event.message || 'HQ Copilot streaming failed.');
        if (event.type === 'complete') complete = event.data;
      }
      if (done) break;
    }
    return { success: true, data: complete };
  }

  async approveAdminAssistantAction(actionId, confirmationToken, typedConfirmation = '') {
    return this.request(`/admin/assistant/actions/${actionId}/approve/`, {
      method: 'POST',
      body: JSON.stringify({ confirmation_token: confirmationToken, typed_confirmation: typedConfirmation }),
    });
  }

  async executeAdminAssistantAction(actionId) {
    return this.request(`/admin/assistant/actions/${actionId}/execute/`, { method: 'POST' });
  }

  async rejectAdminAssistantAction(actionId) {
    return this.request(`/admin/assistant/actions/${actionId}/reject/`, { method: 'POST' });
  }

  async getAdminAssistantActionHistory() {
    return this.request('/admin/assistant/actions/');
  }

  // Admin stats
  async getIntegrityMonitorHealth() {
    return this.request('/tickets/admin/integrity-health/', { noStore: true });
  }

  // ── Ops Center (health issues, guided repairs, alerts, Customer 360) ──
  async getOpsOverview({ eventYear = '' } = {}) {
    const params = new URLSearchParams();
    if (eventYear) params.set('event_year', String(eventYear));
    return this.request(`/tickets/admin/ops/overview/?${params.toString()}`, { noStore: true });
  }

  async getOpsIssues({ eventYear = '', status = 'OPEN', severity = '', code = '', category = '', search = '', sort = '', hideInformational = true, page = 1, perPage = 25 } = {}) {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage), status });
    if (eventYear) params.set('event_year', String(eventYear));
    if (severity) params.set('severity', severity);
    if (code) params.set('code', code);
    if (category) params.set('category', category);
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    params.set('hide_informational', hideInformational ? 'true' : 'false');
    return this.request(`/tickets/admin/ops/issues/?${params.toString()}`, { noStore: true });
  }

  async getOpsStripeFacts({ orderId = '', issueId = '' } = {}) {
    const params = new URLSearchParams(issueId ? { issue_id: issueId } : { order_id: orderId });
    return this.request(`/tickets/admin/ops/stripe-facts/?${params.toString()}`, { noStore: true });
  }

  async getOpsIssue(issueId) {
    return this.request(`/tickets/admin/ops/issues/${issueId}/`, { noStore: true });
  }

  async acknowledgeOpsIssue(issueId, note = '', clear = false) {
    return this.request(`/tickets/admin/ops/issues/${issueId}/`, { method: 'POST', body: JSON.stringify({ note, clear }) });
  }

  async getOpsPlaybooks() {
    return this.request('/tickets/admin/ops/playbooks/', { noStore: true });
  }

  async planOpsPlaybook(key, params = {}) {
    return this.request(`/tickets/admin/ops/playbooks/${key}/plan/`, { method: 'POST', body: JSON.stringify({ params }) });
  }

  async executeOpsPlaybook(key, params = {}, planToken) {
    return this.request(`/tickets/admin/ops/playbooks/${key}/execute/`, { method: 'POST', body: JSON.stringify({ params, plan_token: planToken, confirm: true }) });
  }

  async getOpsPlaybookHistory({ page = 1, perPage = 25, playbook = '' } = {}) {
    const params = new URLSearchParams({ page: String(page), per_page: String(perPage) });
    if (playbook) params.set('playbook', playbook);
    return this.request(`/tickets/admin/ops/playbooks/history/?${params.toString()}`, { noStore: true });
  }

  async getOpsAlertSettings() {
    return this.request('/tickets/admin/ops/alert-settings/', { noStore: true });
  }

  async updateOpsAlertSettings(payload) {
    return this.request('/tickets/admin/ops/alert-settings/', { method: 'PATCH', body: JSON.stringify(payload) });
  }

  async sendOpsTestAlert() {
    return this.request('/tickets/admin/ops/alert-settings/test/', { method: 'POST', body: JSON.stringify({}) });
  }

  async getCustomer360({ email = '', userId = '', timelinePageSize = 25 } = {}) {
    const params = new URLSearchParams({ timeline_page_size: String(timelinePageSize) });
    if (userId) params.set('user_id', userId);
    if (email) params.set('email', email);
    return this.request(`/tickets/admin/ops/customer-360/?${params.toString()}`, { noStore: true });
  }

  async getCustomer360Timeline({ email = '', userId = '', page = 1, pageSize = 25, kinds = [] } = {}) {
    const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
    if (userId) params.set('user_id', userId);
    if (email) params.set('email', email);
    if (kinds.length) params.set('kinds', kinds.join(','));
    return this.request(`/tickets/admin/ops/customer-360/timeline/?${params.toString()}`, { noStore: true });
  }

  async getAdminStats(excludeHidden = false, forceRefresh = false, eventYear = '', options = {}) {
    const searchParams = new URLSearchParams();
    if (excludeHidden) searchParams.set('exclude_hidden', 'true');
    if (forceRefresh) searchParams.set('refresh', 'true');
    if (options.fast) searchParams.set('fast', 'true');
    if (eventYear) searchParams.set('event_year', String(eventYear));
    const params = searchParams.toString() ? `?${searchParams.toString()}` : '';
    return this.request(`/tickets/admin/stats/${params}`, { cacheTtl: forceRefresh ? 0 : options.fast ? 5000 : 15000 });
  }

  async getAdminTodayStats(range = 'today', startDate = '', endDate = '', eventYear = '') {
    const params = new URLSearchParams();
    if (range) params.set('range', range);
    if (eventYear) params.set('event_year', String(eventYear));
    if (range === 'custom' && startDate) params.set('start_date', startDate);
    if (range === 'custom' && endDate) params.set('end_date', endDate);
    const query = params.toString();
    return this.request(`/tickets/admin/stats/today/${query ? `?${query}` : ''}`);
  }

  async getAdminChartData(options = {}) {
    const params = new URLSearchParams();
    if (options.eventYear) params.set('event_year', String(options.eventYear));
    const query = params.toString();
    return this.request(`/tickets/admin/stats/charts/${query ? `?${query}` : ''}`, { cacheTtl: 30000 });
  }

  async getFestivalIntelligence({ eventYear = '', refresh = false } = {}) {
    const params = new URLSearchParams();
    if (eventYear) params.set('event_year', String(eventYear));
    if (refresh) params.set('refresh', 'true');
    const query = params.toString();
    return this.request(`/tickets/admin/festival-intelligence/${query ? `?${query}` : ''}`, { cacheTtl: refresh ? 0 : 60000, noStore: refresh });
  }

  // Comprehensive Sales Analysis (POS + PAC AMP + festival passes + vendors),
  // computed in California (LA) time and reconciled against Stripe.
  async getSalesAnalytics({ refresh = false, eventYear = '' } = {}) {
    const params = new URLSearchParams();
    if (refresh) params.set('refresh', 'stripe');
    if (eventYear) params.set('event_year', String(eventYear));
    const query = params.toString();
    return this.request(`/tickets/admin/sales-analytics/${query ? `?${query}` : ''}`);
  }

  getCharityExportUrl() {
    // Get fresh token from sessionStorage instead of stale constructor value
    const token = sessionStorage.getItem('accessToken') || this.accessToken || '';
    return `${API_BASE_URL}/vendors/admin/charities/export/?token=${token}`;
  }

  getVendorDocumentsExportUrl() {
    // Get fresh token from sessionStorage instead of stale constructor value
    const token = sessionStorage.getItem('accessToken') || this.accessToken || '';
    return `${API_BASE_URL}/vendors/admin/documents/export/?token=${token}`;
  }

  // Admin vendor booth number
  async updateVendorBoothNumber(orderId, boothNumber) {
    return this.request(`/tickets/admin/vendor/${orderId}/booth-number/`, {
      method: 'PATCH',
      body: JSON.stringify({ booth_number: boothNumber })
    });
  }

  // Admin vendor booth details (license plate, product type)
  async updateVendorBoothDetails(orderId, data) {
    return this.request(`/tickets/admin/vendor/${orderId}/booth-details/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  // Admin vendor contract reminder
  async sendVendorContractReminder(orderId) {
    return this.request(`/tickets/admin/vendor/${orderId}/contract-reminder/`, {
      method: 'POST'
    });
  }

  // Admin vendor missing docs reminder
  async sendVendorMissingDocsReminder(orderId) {
    return this.request(`/tickets/admin/vendor/${orderId}/missing-docs-reminder/`, {
      method: 'POST'
    });
  }

  // Admin remove vendor order (refund + restock + delete)
  async removeVendorOrder(orderId) {
    return this.request(`/tickets/admin/vendor/${orderId}/remove/`, {
      method: 'POST'
    });
  }

  // Admin vendor approve/reject
  async approveVendor(orderId, confirmEmail, confirmTotal) {
    return this.request(`/tickets/admin/vendor/${orderId}/approve/`, {
      method: 'POST',
      body: JSON.stringify({ confirm_email: confirmEmail, confirm_total: confirmTotal })
    });
  }

  async toggleOrderHidden(orderId) {
    return this.request(`/tickets/admin/order/${orderId}/toggle-hidden/`, {
      method: 'POST'
    });
  }

  async resendOrderConfirmation(orderId) {
    return this.request(`/tickets/coordinator/resend-confirmation/${orderId}/`, {
      method: 'POST'
    });
  }

  async getEmailPreview(orderId) {
    return this.request(`/tickets/coordinator/email-preview/${orderId}/`);
  }

  // Admin: Send custom email
  async sendAdminEmail(data) {
    return this.request('/tickets/admin/send-email/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async deleteOrder(orderId, confirmEmail, confirmTotal) {
    return this.request(`/tickets/admin/order/${orderId}/delete/`, {
      method: 'POST',
      body: JSON.stringify({ confirm_email: confirmEmail, confirm_total: confirmTotal })
    });
  }

  async rejectVendor(orderId, confirmEmail, confirmTotal) {
    return this.request(`/tickets/admin/vendor/${orderId}/reject/`, {
      method: 'POST',
      body: JSON.stringify({ confirm_email: confirmEmail, confirm_total: confirmTotal })
    });
  }

  async refundVendorWithStatus(orderId, confirmEmail, confirmTotal, targetVendorStatus) {
    return this.request(`/tickets/admin/vendor/${orderId}/refund-status/`, {
      method: 'POST',
      body: JSON.stringify({
        confirm_email: confirmEmail,
        confirm_total: confirmTotal,
        target_vendor_status: targetVendorStatus,
      })
    });
  }

  // Admin create account
  async adminCreateAccount({ email, full_name, password, role, phone }) {
    return this.request('/tickets/admin/create-account/', {
      method: 'POST',
      body: JSON.stringify({ email, full_name, password, role, phone })
    });
  }

  // Pricing adjustment refunds (60% price reduction)
  async getPricingRefunds() {
    return this.request('/tickets/admin/pricing-refunds/');
  }

  async processPricingRefund(orderId) {
    return this.request(`/tickets/admin/pricing-refunds/${orderId}/process/`, {
      method: 'POST',
    });
  }

  // ==================== PARTNER APPLICATION ====================

  async submitPartnerApplication(data) {
    return this.request('/vendors/partner-application/', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true
    });
  }

  // ==================== MEDIA RELATIONS ====================

  async submitMediaInquiry(data) {
    return this.request('/config/media/', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    });
  }

  async getAdminMediaInquiries() {
    return this.request('/config/admin/media/', { cacheTtl: 15000 });
  }

  async createAdminMediaInquiry(data) {
    return this.request('/config/admin/media/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Public: dropdown options shown on /media (active only, grouped by category)
  async getMediaFormOptions() {
    return this.request('/config/media-options/', { skipAuth: true });
  }

  // Admin/staff: manage MediaFormOption rows (full list incl. inactive)
  async getAdminMediaFormOptions() {
    return this.request('/config/admin/media-options/', { cacheTtl: 60000 });
  }

  async createAdminMediaFormOption(data) {
    return this.request('/config/admin/media-options/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminMediaFormOption(id, patch) {
    return this.request(`/config/admin/media-options/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async deleteAdminMediaFormOption(id) {
    return this.request(`/config/admin/media-options/${id}/`, { method: 'DELETE' });
  }

  // Staff dashboard tab permissions (admin manages, staff reads)
  async getAdminEventConfig() {
    return this.request('/config/admin/config/', { cacheTtl: 30000 });
  }

  async prepareAdminEventYear(eventYear) {
    return this.request('/config/admin/config/prepare-event-year/', {
      method: 'POST',
      body: JSON.stringify({ event_year: eventYear }),
    });
  }

  async updateAdminEventConfig(patch) {
    return this.request('/config/admin/config/', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async getStaffAllowedTabs() {
    return this.request('/config/admin/staff-allowed-tabs/', { cacheTtl: 30000 });
  }

  async updateStaffAllowedTabs(tabs) {
    return this.request('/config/admin/staff-allowed-tabs/', {
      method: 'PATCH',
      body: JSON.stringify({ tabs }),
    });
  }

  async updateAdminMediaInquiry(id, patch) {
    return this.request(`/config/admin/media/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async deleteAdminMediaInquiry(id) {
    return this.request(`/config/admin/media/${id}/`, { method: 'DELETE' });
  }

  // ==================== VIP REGISTRATIONS ====================
  // Mirrors the Media endpoints exactly — VIPs get the same form structure
  // plus optional seat assignment via /tickets/admin/seat-assignments/.

  async submitVipRegistration(data) {
    return this.request('/config/vip/', {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    });
  }

  async getVipFormOptions() {
    return this.request('/config/vip-options/', { skipAuth: true });
  }

  async getAdminVipRegistrations() {
    return this.request('/config/admin/vip/', { cacheTtl: 15000 });
  }

  async createAdminVipRegistration(data) {
    return this.request('/config/admin/vip/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminVipRegistration(id, patch) {
    return this.request(`/config/admin/vip/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async deleteAdminVipRegistration(id) {
    return this.request(`/config/admin/vip/${id}/`, { method: 'DELETE' });
  }

  async getAdminVipFormOptions() {
    return this.request('/config/admin/vip-options/', { cacheTtl: 60000 });
  }

  async createAdminVipFormOption(data) {
    return this.request('/config/admin/vip-options/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminVipFormOption(id, patch) {
    return this.request(`/config/admin/vip-options/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async deleteAdminVipFormOption(id) {
    return this.request(`/config/admin/vip-options/${id}/`, { method: 'DELETE' });
  }

  // ==================== PUBLIC VIP CHECK-IN (PIN-protected) ====================

  async getVipCheckinList(pin) {
    return this.request(`/config/vip-checkin/?pin=${encodeURIComponent(pin)}`, { skipAuth: true });
  }

  async updateVipCheckin(id, pin, data) {
    return this.request(`/config/vip-checkin/${id}/?pin=${encodeURIComponent(pin)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      skipAuth: true,
    });
  }

  // ==================== PUBLIC MEDIA CHECK-IN (PIN-protected) ====================

  async getMediaCheckinList(pin) {
    return this.request(`/config/media-checkin/?pin=${encodeURIComponent(pin)}`, { skipAuth: true });
  }

  async updateMediaCheckin(id, pin, data) {
    return this.request(`/config/media-checkin/${id}/?pin=${encodeURIComponent(pin)}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
      skipAuth: true,
    });
  }

  // ==================== SEAT ASSIGNMENT (Media + VIP -> blocked AMP seats) ====================

  /**
   * List the AmphitheaterTickets owned by a buyer email (e.g. samaresheh@gmail.com)
   * grouped by event_date + section. Used by the "Assign Seat" modal.
   * @param {string} ownerEmail - the buyer email whose blocked seats to fetch
   * @param {string} [eventDate] - optional YYYY-MM-DD filter
   */
  async getAdminBlockedSeats(ownerEmail, eventDate, options = {}) {
    const params = new URLSearchParams({ owner_email: ownerEmail });
    if (eventDate) params.set('event_date', eventDate);
    if (options?.poolKey) params.set('pool_key', options.poolKey);
    const endpoint = `/tickets/admin/blocked-seats/?${params.toString()}`;
    const pendingKey = `${endpoint}|${this.getToken() || 'anonymous'}`;
    const pending = this.pendingBlockedSeatRequests.get(pendingKey);
    if (pending) return pending;
    const request = this.request(endpoint, { cacheTtl: 60000 });
    this.pendingBlockedSeatRequests.set(pendingKey, request);
    try {
      return await request;
    } finally {
      if (this.pendingBlockedSeatRequests.get(pendingKey) === request) {
        this.pendingBlockedSeatRequests.delete(pendingKey);
      }
    }
  }

  /**
   * List existing seat assignments. Optional filters: source, source-record id, include_revoked.
   */
  async getAdminSeatAssignments({ source, mediaInquiryId, vipRegistrationId, includeRevoked, assignmentKind } = {}) {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (mediaInquiryId) params.set('media_inquiry_id', mediaInquiryId);
    if (vipRegistrationId) params.set('vip_registration_id', vipRegistrationId);
    if (includeRevoked) params.set('include_revoked', '1');
    if (assignmentKind) params.set('assignment_kind', assignmentKind);
    const qs = params.toString();
    return this.request(`/tickets/admin/seat-assignments/${qs ? '?' + qs : ''}`, { cacheTtl: 10000 });
  }

  /**
   * Create a seat assignment - reassigns Sam's seat to a Media/VIP attendee.
   * Required: ticket_id (or amphitheater_ticket_id), source, attendee_email.
   * Source-specific: media_inquiry_id (when source='media'), vip_registration_id (when source='vip').
   */
  async createAdminSeatAssignment(data) {
    return this.request('/tickets/admin/seat-assignments/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Revoke a seat assignment - restores original owner, invalidates assignee QR.
   */
  async revokeAdminSeatAssignment(id) {
    return this.request(`/tickets/admin/seat-assignments/${id}/`, { method: 'DELETE' });
  }

  async updateAdminWristbandAssignment(batchId, data) {
    return this.request(`/tickets/admin/wristband-assignments/${batchId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminWristbandAssignment(batchId) {
    return this.request(`/tickets/admin/wristband-assignments/${batchId}/`, { method: 'DELETE' });
  }

  async deleteAdminFestivalPassAssignment(batchId) {
    return this.request(`/tickets/admin/festival-pass-assignments/${batchId}/`, { method: 'DELETE' });
  }

  /**
   * List all ISSUED tickets owned by a given email — powers the Ticket Swap tab.
   * @param {string} email - the owner email to look up
   * @param {('buyer'|'owner')} [match='buyer'] - match by Order.buyer.email or Ticket.owner.email
   */
  async getAdminOwnerTickets(email, match = 'buyer') {
    const params = new URLSearchParams({ email, match });
    return this.request(`/tickets/admin/owner-tickets/?${params.toString()}`);
  }

  async getAdminOwnerEmailSuggestions(query, match = 'buyer', limit = 10) {
    const params = new URLSearchParams({ q: query || '', match, limit: String(limit || 10) });
    return this.request(`/tickets/admin/owner-email-suggestions/?${params.toString()}`);
  }

  async sendAdminOwnerTicketEmail({ ticketId, toEmail } = {}) {
    return this.request('/tickets/admin/owner-tickets/send-email/', {
      method: 'POST',
      body: JSON.stringify({ ticket_id: ticketId, to_email: toEmail || '' }),
    });
  }

  async assignAdminUnsoldSeatOrPass(data) {
    return this.request('/tickets/admin/unsold-assignments/', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getAdminUnsoldAssignments({ assignmentType, includeRevoked, eventYear } = {}) {
    const params = new URLSearchParams();
    if (assignmentType) params.set('assignment_type', assignmentType);
    if (includeRevoked) params.set('include_revoked', '1');
    if (eventYear) params.set('event_year', String(eventYear));
    const qs = params.toString();
    return this.request(`/tickets/admin/unsold-assignments/${qs ? '?' + qs : ''}`);
  }

  async revokeAdminUnsoldAssignment(orderId) {
    return this.request(`/tickets/admin/unsold-assignments/${orderId}/?release_to_public=1`, { method: 'DELETE' });
  }

  async sendAdminUnsoldAssignmentEmail(orderId) {
    return this.request(`/tickets/admin/unsold-assignments/${orderId}/send-email/`, { method: 'POST' });
  }

  // Backward-compatible alias used by AdminDashboard.
  async getEventDates() {
    return this.getAmphitheaterEventDates();
  }

  // ==================== HIRING OPPORTUNITIES ====================

  async submitHiringApplication(formData) {
    // FormData (multipart) so we can include the optional resume PDF.
    const url = `${API_BASE_URL}/config/hiring/`;
    const response = await fetch(url, {
      method: 'POST',
      body: formData,
    });
    return response.json();
  }

  async getAdminHiringSubmissions() {
    return this.request('/config/admin/hiring/');
  }

  async updateAdminHiringSubmission(id, patch) {
    return this.request(`/config/admin/hiring/${id}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async deleteAdminHiringSubmission(id) {
    return this.request(`/config/admin/hiring/${id}/`, { method: 'DELETE' });
  }

  // ==================== POST-FESTIVAL SURVEYS ====================

  async getPostFestivalSurveyInvite(inviteId) {
    return this.request(`/config/surveys/post-festival/invites/${inviteId}/`, { skipAuth: true });
  }

  async submitPostFestivalSurvey(inviteId, data) {
    return this.request(`/config/surveys/post-festival/invites/${inviteId}/submit/`, {
      method: 'POST',
      body: JSON.stringify(data || {}),
      skipAuth: true,
    });
  }

  async getAdminPostFestivalSurveyInvites({ surveyType, limit } = {}) {
    const params = new URLSearchParams();
    if (surveyType) params.set('survey_type', surveyType);
    if (limit) params.set('limit', limit);
    const qs = params.toString();
    return this.request(`/config/admin/surveys/post-festival/invites/${qs ? `?${qs}` : ''}`);
  }

  async createAdminPostFestivalSurveyInvite(data) {
    return this.request('/config/admin/surveys/post-festival/invites/', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async getAdminPostFestivalSurveyResults({ surveyType, search } = {}) {
    const params = new URLSearchParams();
    if (surveyType) params.set('survey_type', surveyType);
    if (search) params.set('search', search);
    const qs = params.toString();
    return this.request(`/config/admin/surveys/post-festival/results/${qs ? `?${qs}` : ''}`);
  }

  // ==================== AFFILIATE PROGRAM ====================

  async getAffiliates(options = {}) {
    const params = new URLSearchParams();
    if (options.page) params.set('page', String(options.page));
    if (options.perPage) params.set('per_page', String(options.perPage));
    if (options.search) params.set('search', String(options.search));
    if (options.scope) params.set('scope', String(options.scope));
    const query = params.toString();
    return this.request(`/payments/affiliates/${query ? `?${query}` : ''}`);
  }

  async createAffiliate(data) {
    return this.request('/payments/affiliates/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async getAffiliateDetail(affiliateId) {
    return this.request(`/payments/affiliates/${affiliateId}/`);
  }

  async updateAffiliate(affiliateId, data) {
    return this.request(`/payments/affiliates/${affiliateId}/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async deleteAffiliate(affiliateId) {
    return this.request(`/payments/affiliates/${affiliateId}/`, {
      method: 'DELETE'
    });
  }

  async deactivateAffiliate(affiliateId) {
    return this.request(`/payments/affiliates/${affiliateId}/deactivate/`, {
      method: 'POST'
    });
  }

  async getAffiliateStats() {
    return this.request('/payments/affiliates/stats/');
  }

  async createAffiliateWithCoupon(data) {
    return this.request('/payments/affiliates/create-with-coupon/', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async updateAffiliateCoupon(affiliateId, data) {
    return this.request(`/payments/affiliates/${affiliateId}/update-coupon/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async getMyAffiliateInfo() {
    return this.request('/payments/affiliates/me/');
  }

  async updateMyAffiliateInfo(data) {
    return this.request('/payments/affiliates/me/', {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  }

  async getAffiliatePayouts(affiliateId) {
    return this.request(`/payments/affiliates/${affiliateId}/payouts/`);
  }

  async markAffiliatePaid(affiliateId, data = {}) {
    return this.request(`/payments/affiliates/${affiliateId}/mark-paid/`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async recordAffiliatePartialPayment(affiliateId, data = {}) {
    return this.request(`/payments/affiliates/${affiliateId}/record-partial-payment/`, {
      method: 'POST',
      body: JSON.stringify(data)
    });
  }

  async sendZelleReminder(affiliateId) {
    return this.request(`/payments/affiliates/${affiliateId}/zelle-reminder/`, {
      method: 'POST',
    });
  }

  async getMyAffiliatePayouts() {
    return this.request('/payments/affiliates/me/payouts/');
  }

  async getAdminAllPayouts() {
    return this.request('/payments/affiliates/admin-payouts/');
  }

  // ==================== TOOLKIT IMAGES ====================

  async getToolkitImages(layoutVersion, couponCode) {
    const params = new URLSearchParams();
    if (layoutVersion) params.append('layout_version', layoutVersion);
    if (couponCode) params.append('coupon_code', couponCode);
    const qs = params.toString();
    return this.request(`/auth/toolkit-images/${qs ? '?' + qs : ''}`);
  }

  async generateToolkitImages(couponCode, discountPercent) {
    return this.request('/auth/toolkit-images/', {
      method: 'PUT',
      body: JSON.stringify({ coupon_code: couponCode, discount_percent: discountPercent }),
    });
  }

  async deleteToolkitImage(assetKey) {
    return this.request(`/auth/toolkit-images/?asset_key=${encodeURIComponent(assetKey)}`, {
      method: 'DELETE',
    });
  }

  async trackToolkitDownload(assetKey) {
    return this.request('/auth/toolkit-download-track/', {
      method: 'POST',
      body: JSON.stringify({ asset_key: assetKey }),
    });
  }

  async getToolkitImageProxy(assetKey) {
    const url = `${API_BASE_URL}/auth/toolkit-images/proxy/?asset_key=${encodeURIComponent(assetKey)}`;
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { headers });
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  }

  async saveToolkitImages(imageBlobs, layoutVersion, couponCode) {
    const formData = new FormData();
    formData.append('layout_version', layoutVersion || '');
    formData.append('coupon_code', couponCode || '');
    for (const [key, blob] of Object.entries(imageBlobs)) {
      formData.append(key, blob, `${key}.jpg`);
    }
    const url = `${API_BASE_URL}/auth/toolkit-images/`;
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    return response.json();
  }

  // ==================== CMS CONTENT ====================

  async getCmsContent() {
    return this.request('/config/cms/', { skipAuth: true });
  }

  async getAdminCmsContent() {
    return this.request('/config/admin/cms/');
  }

  async saveCmsContent(data) {
    return this.request('/config/admin/cms/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getCmsImages() {
    return this.request('/config/admin/cms/images/');
  }

  async uploadCmsImage(key, imageFile, altText = '', usedOn = '') {
    const formData = new FormData();
    formData.append('key', key);
    formData.append('image', imageFile);
    if (altText) formData.append('alt_text', altText);
    if (usedOn) formData.append('used_on', usedOn);

    const url = `${API_BASE_URL}/config/admin/cms/images/`;
    const token = sessionStorage.getItem('accessToken') || this.accessToken;
    const headers = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    return response.json();
  }

  // ── Segments CRM ──────────────────────────────────────────────
  async getSegmentsMeta() {
    return this.request('/tickets/admin/segments/meta/');
  }

  async querySegment(params = {}) {
    return this.request('/tickets/admin/segments/query/', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async segmentAction(data) {
    return this.request('/tickets/admin/segments/query/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // ── Segment Builder (new) ────────────────────────────────────
  async getSegmentBuilderMeta() {
    return this.request('/tickets/admin/segment-builder/meta/');
  }

  async segmentBuilderQuery(data) {
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    return this.request('/tickets/admin/segment-builder/query/', {
      method: 'POST',
      body: isFormData ? data : JSON.stringify(data),
    });
  }

  async generateSegmentWithAI(data) {
    return this.request('/tickets/admin/segment-builder/ai-generate/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async getSavedSegments() {
    return this.request('/tickets/admin/segment-builder/saved/');
  }

  async createSavedSegment(data) {
    return this.request('/tickets/admin/segment-builder/saved/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateSavedSegment(id, data) {
    return this.request(`/tickets/admin/segment-builder/saved/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteSavedSegment(id) {
    return this.request(`/tickets/admin/segment-builder/saved/${id}/`, {
      method: 'DELETE',
    });
  }

  async getEmailTemplates() {
    return this.request('/tickets/admin/segment-builder/templates/');
  }

  async createEmailTemplate(data) {
    return this.request('/tickets/admin/segment-builder/templates/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateEmailTemplate(id, data) {
    return this.request(`/tickets/admin/segment-builder/templates/${id}/`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteEmailTemplate(id) {
    return this.request(`/tickets/admin/segment-builder/templates/${id}/`, {
      method: 'DELETE',
    });
  }

  async getCampaignHistory() {
    return this.request('/tickets/admin/segment-builder/campaigns/');
  }

  async getCampaignHistoryDetail(campaignId) {
    return this.request(`/tickets/admin/segment-builder/campaigns/${campaignId}/`);
  }

  // ── Activity Logs ────────────────────────────────────────────
  async getActivityLogs(params = {}) {
    const qs = new URLSearchParams();
    if (params.page) qs.set('page', params.page);
    if (params.per_page) qs.set('per_page', params.per_page);
    if (params.category) qs.set('category', params.category);
    if (params.level) qs.set('level', params.level);
    if (params.search) qs.set('search', params.search);
    if (params.since) qs.set('since', params.since);
    if (params.before) qs.set('before', params.before);
    if (params.actor_email) qs.set('actor_email', params.actor_email);
    if (params.target_type) qs.set('target_type', params.target_type);
    const query = qs.toString();
    return this.request(`/admin/activity-logs/${query ? '?' + query : ''}`);
  }

  async getActivityLogStats() {
    return this.request('/admin/activity-logs/stats/');
  }

  // ── Stripe Terminal / POS ─────────────────────────────────────
  async posGetLocations() {
    return this.request('/terminal/locations/');
  }

  async posEnsureLocation() {
    return this.request('/terminal/locations/', { method: 'POST', body: JSON.stringify({}) });
  }

  async posGetReaders(sync = false) {
    return this.request(`/terminal/readers/${sync ? '?sync=1' : ''}`);
  }

  async posRegisterReader(registrationCode, label = '') {
    return this.request('/terminal/readers/register/', {
      method: 'POST',
      body: JSON.stringify({ registration_code: registrationCode, label }),
    });
  }

  async posRegisterSimulatedReader(label = 'Simulator') {
    return this.request('/terminal/readers/register-simulated/', {
      method: 'POST',
      body: JSON.stringify({ label }),
    });
  }

  async posDeleteReader(readerId) {
    return this.request(`/terminal/readers/${readerId}/`, { method: 'DELETE' });
  }

  async posUpdateReader(readerId, { label, assigned_cashier_id } = {}) {
    const payload = {};
    if (label !== undefined) payload.label = label;
    if (assigned_cashier_id !== undefined) payload.assigned_cashier_id = assigned_cashier_id;
    return this.request(`/terminal/readers/${readerId}/`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    });
  }

  async posGetProducts() {
    return this.request('/terminal/products/');
  }

  async posGetManualProductCatalog() {
    return this.request('/terminal/manual-products/', { cacheTtl: 30000 });
  }

  async posSaveManualProductCatalog(products, categories) {
    return this.request('/terminal/manual-products/', {
      method: 'PUT',
      body: JSON.stringify({ products, categories }),
    });
  }

  /**
   * Create a POS charge.
   *
   * @param {string} readerId
   * @param {Array<{ticket_type_id: string, quantity: number, metadata?: object}>} items
   * @param {{email?: string, full_name?: string, phone?: string}} customer
   * @param {object} [options]
   * @param {string}  [options.couponCode]  Public coupon code
   * @param {'staff'|'self'} [options.mode]  Operating mode of the device
   * @param {{kind:'none'|'comp'|'percent', percent?:number, reason?:string}} [options.discount]
   *        Cashier-initiated discount (COMP zeroes the total + bypasses
   *        Stripe Terminal; percent discounts flow through to the reader).
   */
  async posCreateCharge(readerId, items, customer, options = {}) {
    // Back-compat: allow the old 4th-arg-is-couponCode signature.
    if (typeof options === 'string') options = { couponCode: options };
    const {
      couponCode = '',
      mode = 'staff',
      discount = null,
    } = options;
    const body = {
      reader_id: readerId,
      items,
      customer,
      coupon_code: couponCode || '',
      mode,
    };
    if (discount) body.discount = discount;
    return this.request('/terminal/charge/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async posCreateAmpCharge(readerId, holdIds, customer, options = {}) {
    if (typeof options === 'string') options = { couponCode: options };
    const {
      couponCode = '',
      mode = 'staff',
      discount = null,
    } = options;
    const body = {
      reader_id: readerId,
      hold_ids: holdIds,
      customer,
      coupon_code: couponCode || '',
      mode,
    };
    if (discount) body.discount = discount;
    return this.request('/terminal/charge/amp/', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async posCreateManualCharge(readerId, items, customer = {}, options = {}) {
    const {
      taxPercent = 0,
      mode = 'staff',
    } = options || {};
    return this.request('/terminal/charge/manual/', {
      method: 'POST',
      body: JSON.stringify({
        reader_id: readerId,
        items,
        customer,
        tax_percent: taxPercent,
        mode,
      }),
    });
  }

  // ── Amphitheater (public endpoints, reused by POS seat picker) ───
  async ampGetSeats(eventDate, opts = {}) {
    const qs = new URLSearchParams({ event_date: eventDate });
    if (opts.includeAvailable !== false) qs.set('include_available', 'true');
    return this.request(`/tickets/amphitheater/seats/?${qs.toString()}`);
  }

  async ampCreateHold({ sectionId, eventDate, quantity, seats, sessionKey }) {
    return this.request('/tickets/amphitheater/seats/hold/', {
      method: 'POST',
      body: JSON.stringify({
        section_id: sectionId,
        event_date: eventDate,
        quantity,
        seats,
        session_key: sessionKey,
      }),
    });
  }

  async ampReleaseHold(holdId) {
    return this.request(`/tickets/amphitheater/holds/${holdId}/release/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async posGetChargeStatus(orderId) {
    return this.request(`/terminal/charge/${orderId}/status/`);
  }

  async posCancelCharge(orderId) {
    return this.request(`/terminal/charge/${orderId}/cancel/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async posGetRecentSales({ mine = false, limit = 25, offset = 0 } = {}) {
    const qs = new URLSearchParams();
    if (mine) qs.set('mine', '1');
    if (limit) qs.set('limit', String(limit));
    if (offset) qs.set('offset', String(offset));
    return this.request(`/terminal/sales/?${qs.toString()}`);
  }

  /**
   * Fetch the live POS inventory snapshot (admin only).
   * Returns { totals, groups: [{category, items:[{slug,sku,name,sold,
   * remaining,inventory_total,sold_pct,...}, ...]}, ...], items:[...] }.
   */
  async posGetInventory() {
    return this.request('/terminal/inventory/');
  }

  /**
   * Update the effective stock for a single venue-catalog SKU
   * (merch / ride / fifa). Writes to ``TicketType.capacity`` server-side.
   * Pass ``inventoryTotal: null`` to reset the SKU back to the catalog
   * default. Admin-only.
   *
   * @param {string} slug            SKU slug (e.g. 'merch-arttee-s')
   * @param {number|null} inventoryTotal  New cap (>=0) or null to reset
   */
  async posUpdateInventory(slug, inventoryTotal) {
    return this.request('/terminal/inventory/update/', {
      method: 'POST',
      body: JSON.stringify({
        slug,
        inventory_total: inventoryTotal,
      }),
    });
  }

  /**
   * Fetch aggregated POS analytics for the admin dashboard.
   *
   * @param {Object} [opts]
   * @param {string} [opts.start]      ISO datetime lower bound
   * @param {string} [opts.end]        ISO datetime upper bound
   * @param {string} [opts.cashierId]  UUID — if set, filter to one cashier
   *                                   (superuser-only; staff are auto-scoped
   *                                   to themselves server-side).
   * @param {string} [opts.mode]       'staff' | 'self' — narrow to
   *                                   cashier-operated vs customer-operated
   *                                   sales. Empty string = both.
   * @param {string} [opts.flow]       'products' | 'catalog' — narrow to
   *                                   current /pos products flow vs other
   *                                   POS flows. Empty string = both.
   */
  async posGetAnalytics({ start = '', end = '', cashierId = '', mode = '', flow = '', eventYear = '' } = {}) {
    const qs = new URLSearchParams();
    if (start)      qs.set('start',      start);
    if (end)        qs.set('end',        end);
    if (cashierId)  qs.set('cashier_id', cashierId);
    if (mode)       qs.set('mode',       mode);
    if (flow)       qs.set('flow',       flow);
    if (eventYear)  qs.set('event_year', String(eventYear));
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/terminal/analytics/${suffix}`);
  }

  /**
   * Fetch the FIFA watch-party match schedule for the hidden ``/fifa``
   * booking page. No auth required; works for anonymous visitors.
   */
  async getPublicFifaMatches() {
    return this.request('/terminal/public/fifa-matches/');
  }

  async posSimulatorTap(readerId, cardType = 'visa') {
    return this.request('/terminal/simulator/present-card/', {
      method: 'POST',
      body: JSON.stringify({ reader_id: readerId, card_type: cardType }),
    });
  }

  // ── Hookah Lounge Reservations ────────────────────────────────
  // Public endpoints (no auth required)
  async hookahGetConfig() {
    return this.request('/hookah/config/', { skipAuth: true });
  }

  async hookahGetAvailability(date) {
    const qs = new URLSearchParams();
    if (date) qs.set('date', date);
    return this.request(`/hookah/availability/?${qs.toString()}`, { skipAuth: true });
  }

  async hookahCreateReservation(payload) {
    // payload = { event_date, start_time, duration_minutes?, end_time?,
    //             party_size, customer_name,
    //             customer_email?, customer_phone?, notes? }
    return this.request('/hookah/reservations/', {
      method: 'POST',
      body: JSON.stringify(payload),
      skipAuth: true,
    });
  }

  async hookahLookupReservation(tokenOrCode) {
    return this.request(`/hookah/reservations/lookup/${encodeURIComponent(tokenOrCode)}/`, { skipAuth: true });
  }

  // Staff endpoints (requires IsStaffOrAdmin)
  async hookahStaffListReservations({ date = '', status: filterStatus = '', q = '' } = {}) {
    const qs = new URLSearchParams();
    if (date) qs.set('date', date);
    if (filterStatus) qs.set('status', filterStatus);
    if (q) qs.set('q', q);
    const suffix = qs.toString() ? `?${qs.toString()}` : '';
    return this.request(`/hookah/staff/reservations/${suffix}`);
  }

  async hookahStaffCreateReservation(payload) {
    return this.request('/hookah/staff/reservations/create/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async hookahStaffScan(tokenOrCode) {
    return this.request('/hookah/staff/scan/', {
      method: 'POST',
      body: JSON.stringify({ token: tokenOrCode }),
    });
  }

  async hookahStaffCheckIn(reservationId, body = {}) {
    return this.request(`/hookah/staff/reservations/${reservationId}/check-in/`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async hookahStaffReservationAction(reservationId, action) {
    // action: 'cancel' | 'no_show' | 'reopen'
    return this.request(`/hookah/staff/reservations/${reservationId}/action/`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  async hookahResendEmail(reservationId) {
    return this.request(`/hookah/staff/reservations/${reservationId}/resend-email/`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  // Admin endpoints (requires IsAdmin)
  async hookahAdminGetConfig() {
    return this.request('/hookah/admin/config/');
  }

  async hookahAdminUpdateConfig(patch) {
    return this.request('/hookah/admin/config/', {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async hookahAdminListTables() {
    return this.request('/hookah/admin/tables/');
  }

  async hookahAdminCreateTable(payload) {
    return this.request('/hookah/admin/tables/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  }

  async hookahAdminUpdateTable(tableId, patch) {
    return this.request(`/hookah/admin/tables/${tableId}/`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    });
  }

  async hookahAdminDeleteTable(tableId) {
    return this.request(`/hookah/admin/tables/${tableId}/`, { method: 'DELETE' });
  }

}

// Export singleton instance
const api = new ApiService();
export default api;
